import { Theme } from '@mui/material/styles'
import { textBaseline } from 'src/design-system/textBaseline'

import { icons, layout, typography as typeTokens } from '../tokens.generated'
//
import { CloseIcon } from './CustomIcons'

// ----------------------------------------------------------------------
// Chip metrics come from the theme, not from each chip. A status chip and a
// tag chip must sit at the same height and read on the same baseline in a
// table row, whether or not they carry a glyph.
//
// Two sizes, and nothing between them:
//   small   the status chip: `layout.chipHeight` (24), 10px side inset, a 6px
//           gap to the glyph, caption size at semibold. This is what a screen
//           reaches for by default.
//   medium  the filter chip: `layout.filterChipHeight` (34), the dense
//           control inset, small text. 34 rather than the 32 of a dense
//           control is the value the system publishes and the divergence
//           table records; the handover contradicts itself on it, and this is
//           the side the console already shipped.
//
// The base chip carries no semantic colour. The default fill is the sunken
// surface with secondary ink, and a status colour arrives only through
// `StatusChip`, which is what makes the accidental yellow `Free` pill
// impossible. The default fill is declared as a theme variant rather than a
// compound selector, so it stays one class deep and a `sx` colour on a chip
// (tags carry their own) still wins over it.
// ----------------------------------------------------------------------

/**
 * The status chip's side inset and its glyph-to-label gap. Neither is on the
 * spacing scale: 10 sits between s2 and s3, and 6 between s1 and s2. Both are
 * what a 24px pill needs to read as a pill rather than a box, so they are
 * named here until the token source carries them.
 */
const SIDE_INSET = 10
const GLYPH_GAP = 6

/**
 * MUI's icon margin and the label padding both contribute to the gap, so the
 * icon's right margin is negative by whatever the label inset overshoots:
 * inset + correction = gap.
 */
const glyphGapCorrection = (inset: number) => GLYPH_GAP - inset

export default function Chip(theme: Theme) {
  const insets = (inset: number) => ({
    label: { paddingLeft: inset, paddingRight: inset },
    icon: { marginLeft: inset, marginRight: glyphGapCorrection(inset) },
  })
  const small = insets(SIDE_INSET)
  const medium = insets(layout.controlInsetSmall)

  return {
    MuiChip: {
      defaultProps: {
        // The 24px status chip is what a screen reaches for by default; the
        // 32px filter chip is asked for by name.
        size: 'small' as const,
        deleteIcon: <CloseIcon />,
      },

      variants: [
        {
          props: { variant: 'filled' as const, color: 'default' as const },
          style: {
            backgroundColor: theme.palette.background.neutral,
            color: theme.palette.text.secondary,
          },
        },
      ],

      styleOverrides: {
        root: {
          height: layout.chipHeight,
          borderRadius: theme.radii.pill,
          fontSize: theme.typography.pxToRem(typeTokens.console.caption.size),
          fontWeight: typeTokens.weights.semibold,
          lineHeight: 1,
          // The same baseline with a glyph as without one. A chip's baseline
          // is its first item's, and a glyph has none, so a chip that led
          // with one was set by the glyph's foot wherever chips are lined up
          // by baseline: a phone card's `Tags` sat 3px low of its chips.
          ...textBaseline(),
        },
        // A chip that can be pressed answers like every other surface. The
        // default fill is already the sunken surface, so it takes the step
        // below it; a chip carrying its own fill is left alone, because a
        // filter chip's colour is the thing it is saying.
        clickable: {
          'transition': theme.hover.transition,
          '&:hover': {
            backgroundColor: theme.hover.sunken,
          },
        },
        sizeSmall: {
          height: layout.chipHeight,
        },
        sizeMedium: {
          height: layout.filterChipHeight,
          fontSize: theme.typography.pxToRem(typeTokens.console.small.size),
        },
        label: small.label,
        labelSmall: small.label,
        labelMedium: medium.label,
        // The glyph is part of the label: it takes the chip's ink, never a
        // default grey, and it is drawn at the chip step of the icon grid.
        icon: {
          ...small.icon,
          width: icons.sizes.chip,
          height: icons.sizes.chip,
          color: 'inherit',
        },
        iconSmall: small.icon,
        iconMedium: medium.icon,
        colorDefault: {
          '& .MuiChip-avatarMedium, .MuiChip-avatarSmall': {
            color: theme.palette.text.secondary,
          },
        },
        outlined: {
          'borderColor': theme.palette.grey[500_32],
          '&.MuiChip-colorPrimary': {
            borderColor: theme.palette.primary.main,
          },
          '&.MuiChip-colorSecondary': {
            borderColor: theme.palette.secondary.main,
          },
        },
        // These sit on `<semantic>.dark`, which is not the semantic's fill:
        // it is a text colour for three of the five, and its lightness flips
        // between schemes for two of them. So neither `contrastText`, which is
        // the ink for the fill, nor a constant white is right for all four:
        // white is 2.5:1 on the warning slot and near black is 2.9:1 on the
        // light success slot. The ink is resolved from the background it
        // actually sits on.
        avatarColorInfo: {
          color: theme.palette.getContrastText(theme.palette.info.dark),
          backgroundColor: theme.palette.info.dark,
        },
        avatarColorSuccess: {
          color: theme.palette.getContrastText(theme.palette.success.dark),
          backgroundColor: theme.palette.success.dark,
        },
        avatarColorWarning: {
          color: theme.palette.getContrastText(theme.palette.warning.dark),
          backgroundColor: theme.palette.warning.dark,
        },
        avatarColorError: {
          color: theme.palette.getContrastText(theme.palette.error.dark),
          backgroundColor: theme.palette.error.dark,
        },
      },
    },
  }
}
